// Events
import {
  COLLECTION_FILTER_OPTION_CHECK,
  COLLECTION_FILTER_OPTION_UNCHECK,
  COLLECTION_FILTER_RESET,
  COLLECTION_FILTER_SUBMIT,
  COLLECTION_FILTER_UPDATE,
  COLLECTION_SUBMIT_FILTER,
  MODAL_OPEN,
  TRIGGER_FILTER_SHORTCUT,
} from 'events';
import debounce from 'lodash/debounce';
import { component } from 'picoapp';

// Utils
import choozy from 'utils/choozy';
import { clear, hide, parseJSONScript, show, stringToNode, useTemplate } from 'utils/node';
import OverflowScroller from 'utils/overflow-scroller';

import { dispatch } from '../../utils/theme';

export default component(async (node, ctx) => {
  const {
    checkbox: checkboxes,
    filterClear,
    filterApply,
    activeFiltersContainer,
    activeFiltersList,
    activeFilterTemplate,
    defaultSortBy,
    filterOptionsContainer = [],
  } = choozy(node);

  const { idPrefix = '' } = node.dataset;
  const isSidebarForm = node.dataset.filterContext === 'sidebar';

  const shortcutActiveFilters = document.querySelector('[data-shortcut-active-filters]');

  const form = node;

  let controller;

  const createActiveFilterElement = useTemplate(activeFilterTemplate);

  const lookupFilterLabel = (name, value) => {
    const checkbox = checkboxes?.find((cb) => cb.name === name && cb.value === value);
    let label = value;
    if (checkbox) {
      const labelEl =
        document.querySelector(`label[for="${checkbox.id}"]`) || checkbox.closest('label');
      if (labelEl) label = labelEl.textContent.trim();
    }
    return { name, value, label };
  };

  const buildActiveFilters = () =>
    [...new FormData(form)].map(([name, value]) => lookupFilterLabel(name, value));

  ctx.hydrate({ activeFilters: [...new FormData(form)] });

  const submit = () => {
    ctx.emit(COLLECTION_SUBMIT_FILTER, null, form);
  };

  const reset = () => {
    if (defaultSortBy) {
      defaultSortBy.checked = true;
      defaultSortBy.dispatchEvent(new Event('change', { bubbles: true }));
    }

    checkboxes.forEach((input) => {
      input.checked = false;
      input.dispatchEvent(
        new CustomEvent('change', {
          bubbles: true,
          detail: {
            fromReset: true,
          },
        }),
      );
    });

    dispatch('filter-reset-all');

    if (window.location.search !== '') {
      submit();
    }
  };

  const updateAccordion = (e) => {
    const accordion = e.target.closest('.group\\/accordion');
    if (!accordion) return;

    const selectedFiltersCount = accordion.querySelector('[data-selected-filters-count]');
    const checkedInputsAmount = accordion.querySelectorAll('input:checked').length;

    if (!selectedFiltersCount) return;

    if (checkedInputsAmount === 0) {
      hide(selectedFiltersCount);
    } else {
      selectedFiltersCount.textContent = checkedInputsAmount;
      show(selectedFiltersCount);
    }
  };

  const fetchCountData = debounce(async (formData) => {
    const queryString = new URLSearchParams(formData);
    const currentParams = new URLSearchParams(window.location.search);

    if (currentParams.has('q')) {
      queryString.set('q', currentParams.get('q'));
    }

    if (currentParams.has('type')) {
      queryString.set('type', currentParams.get('type'));
    }

    queryString.set('section_id', 'template-collection-filter-count-data');

    controller = new AbortController();

    try {
      const response = await fetch(`?${queryString.toString()}`, { signal: controller.signal });
      const text = await response.text();

      const [script] = stringToNode(text, [
        '#shopify-section-template-collection-filter-count-data',
      ]);
      const { total, countData } = parseJSONScript(script[0]);

      ctx.emit(COLLECTION_FILTER_UPDATE, { countData, total });
    } catch (err) {
      if (err.name === 'AbortError') return;
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }, 400);

  const handleChange = (e) => {
    if (controller) controller.abort();

    const formData = new FormData(form);

    if (e.target && e.target.value && !e?.detail?.fromReset) {
      dispatch(e.target.checked ? 'filter-select' : 'filter-reset-option', {
        option: e.target.value,
        type: e.target.name.split('.').at(-1),
      });
    }

    ctx.emit(COLLECTION_FILTER_UPDATE, { countData: null, activeFilters: [...formData] });

    updateAccordion(e);
    fetchCountData(formData);
  };

  ctx.on(COLLECTION_FILTER_OPTION_UNCHECK, (state, inputId) => {
    checkboxes.forEach((checkbox) => {
      if (checkbox.id === inputId) {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
      }
    });
  });

  ctx.on(COLLECTION_FILTER_OPTION_CHECK, (state, inputId) => {
    checkboxes.forEach((checkbox) => {
      if (checkbox.id === inputId) {
        checkbox.checked = true;
        checkbox.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
      }
    });
  });

  ctx.on(COLLECTION_FILTER_UPDATE, ({ countData }) => {
    const filters = buildActiveFilters().filter(
      ({ name }) => name !== 'sort_by' && name !== 'filter.v.availability',
    );

    if (activeFiltersList) clear(activeFiltersList);
    if (shortcutActiveFilters) clear(shortcutActiveFilters);
    if (activeFiltersContainer) {
      activeFiltersContainer.hidden = filters.length === 0;
    }

    // Show/hide clear button on desktop in sidebar mode
    if (isSidebarForm && filterClear) {
      const clearButtonContainer = filterClear.parentElement;
      if (clearButtonContainer) {
        if (filters.length === 0) {
          clearButtonContainer.classList.add('max-lg:block', 'lg:hidden');
        } else {
          clearButtonContainer.classList.remove('max-lg:block', 'lg:hidden');
        }
      }
    }

    const buttons = filters.map(({ name, value, label }) =>
      createActiveFilterElement({
        'data-filter-name': `${idPrefix}${name}_${value.replace(/[^a-z0-9.]+/gi, '-').toLowerCase()}`,
        label,
      }),
    );

    if (activeFiltersList) {
      activeFiltersList.innerHTML = buttons.map((button) => button.outerHTML).join('');
    }
    if (shortcutActiveFilters) {
      shortcutActiveFilters.innerHTML = buttons.map((button) => button.outerHTML).join('');
    }

    if (checkboxes && countData) {
      Object.keys(countData).forEach((key) => {
        const optionCheckbox = checkboxes.find((e) => e.id === `${idPrefix}${key}`);
        if (optionCheckbox)
          optionCheckbox[+countData[key] > 0 ? 'removeAttribute' : 'setAttribute']('disabled', '');
      });
    }

    if (filterOptionsContainer) {
      [].concat(filterOptionsContainer).forEach((container) => {
        if (container.querySelectorAll('input:not(:disabled)').length > 1) {
          show(container);
        } else {
          hide(container);
        }
      });

      // Hide accordions where all the filter options containers are hidden
      [...node.querySelectorAll('.group\\/accordion')].forEach((accordion) => {
        if (accordion.contains(defaultSortBy)) return;

        if (
          accordion.querySelectorAll('[data-filter-options-container]:not([hidden])').length > 0
        ) {
          show(accordion);
        } else {
          hide(accordion);
        }
      });
    }
  });

  if (activeFiltersList) {
    activeFiltersList.addEventListener('click', (e) => {
      let clickedButton = e.target;

      if (e.target.tagName !== 'BUTTON') {
        clickedButton = e.target.closest('button');
      }

      if (clickedButton === null || clickedButton?.tagName !== 'BUTTON') return;

      const filterOptionId = clickedButton.dataset.filterName;

      ctx.emit(COLLECTION_FILTER_OPTION_UNCHECK, null, filterOptionId);
    });
  }

  if (shortcutActiveFilters) {
    shortcutActiveFilters.addEventListener('click', (e) => {
      const clickedButton = e.target.closest('button');

      if (clickedButton === null) return;

      const filterOptionId = clickedButton.dataset.filterName;
      ctx.emit(COLLECTION_FILTER_OPTION_UNCHECK, null, filterOptionId);
      ctx.emit(COLLECTION_SUBMIT_FILTER, null, form);
    });
  }

  ctx.on(COLLECTION_FILTER_SUBMIT, submit);
  ctx.on(COLLECTION_FILTER_RESET, reset);

  node.addEventListener('change', (e) => {
    handleChange(e);

    // Desktop sidebar applies immediately on each option change.
    if (
      isSidebarForm &&
      window.matchMedia('(min-width: 1024px)').matches &&
      !e?.detail?.fromReset
    ) {
      submit();
    }
  });
  if (filterClear) filterClear.addEventListener('click', reset);
  if (filterApply) filterApply.addEventListener('click', submit);

  OverflowScroller(node);

  ctx.on(TRIGGER_FILTER_SHORTCUT, (_, blockId) => {
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    const isSidebarForm = node.closest('[data-filter-sidebar-content]');

    if (isDesktop && isSidebarForm) {
      document.dispatchEvent(new CustomEvent('filter-sidebar:open'));
    } else if (!isDesktop) {
      ctx.emit(MODAL_OPEN, null, { id: 'collection-filter-modal' });
    }

    const accordions = Array.from(node.querySelectorAll('[data-accordion]'));
    const match = accordions.find((element) => element.dataset.blockId === blockId);
    if (match) {
      const toggle = match.querySelector('[data-toggle]');
      if (toggle && !match.classList.contains('open')) {
        toggle.click();
      }
    }
  });
});
